import { TokenUsageWithBreakdown } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { EnrichedWorkspace, ModelFilterOption, User, UserFilterOption, WorkspaceFilterOption } from '../types';
import { normalizeGroupId } from './tokenDataTransformers';

/**
 * Extract unique model names from MODEL-grouped token usage data
 *
 * @param usageData Token usage data grouped by MODEL
 * @returns Array of model filter options sorted alphabetically
 */
export function extractModelFilterOptions(usageData: TokenUsageWithBreakdown[] | undefined): ModelFilterOption[] {
  if (!usageData || usageData.length === 0) {
    return [];
  }

  const uniqueModels = new Set<string>();

  // Extract unique models from usage data
  usageData.forEach((usageItem) => {
    const usageBreakdown = usageItem.usageBreakdown || [];
    usageBreakdown.forEach((breakdown) => {
      if (breakdown.group) {
        uniqueModels.add(breakdown.group);
      }
    });
  });

  // Convert to filter options and sort
  const options: ModelFilterOption[] = Array.from(uniqueModels)
    .map((model) => ({ key: model, text: model }))
    .sort((a, b) => a.text.localeCompare(b.text));

  return options;
}

/**
 * Extract unique workspace UUIDs from WORKSPACE-grouped token usage data
 * and convert them to workspace filter options with display names
 *
 * @param usageData Token usage data grouped by WORKSPACE
 * @param workspacesMap Map of workspace UUIDs to enriched workspace objects
 * @returns Array of workspace filter options sorted alphabetically
 */
export function extractTokenWorkspaceFilterOptions(
  usageData: TokenUsageWithBreakdown[] | undefined,
  workspacesMap: Map<string, EnrichedWorkspace>
): WorkspaceFilterOption[] {
  if (!usageData || usageData.length === 0) {
    return [];
  }

  const uniqueWorkspaceUUIDs = new Set<string>();

  // Extract unique workspace UUIDs from usage data
  // H2OGPTe API returns workspace groups with prefix (e.g., "workspaces/uuid"),
  // so we normalize to get the raw UUID for consistent lookup
  usageData.forEach((usageItem) => {
    const usageBreakdown = usageItem.usageBreakdown || [];
    usageBreakdown.forEach((breakdown) => {
      if (breakdown.group) {
        // Normalize to strip any prefix (e.g., "workspaces/uuid" -> "uuid")
        const normalizedId = normalizeGroupId(breakdown.group);
        uniqueWorkspaceUUIDs.add(normalizedId);
      }
    });
  });

  // Convert UUIDs to filter options with display names
  const options: WorkspaceFilterOption[] = [];
  uniqueWorkspaceUUIDs.forEach((uuid) => {
    const workspace = workspacesMap.get(`workspaces/${uuid}`);
    if (workspace) {
      let displayName = workspace.displayName;
      // Handle Personal Workspace naming
      if (workspace.displayName === 'Personal Workspace') {
        displayName = `PW - ${workspace.userDisplayName}`;
      }

      options.push({
        key: uuid,
        text: displayName,
      });
    } else {
      // Workspace not found in map, use UUID as fallback
      console.warn('[extractTokenWorkspaceFilterOptions] Workspace not found in map:', uuid);
      options.push({
        key: uuid,
        text: uuid,
      });
    }
  });

  // Sort alphabetically by display name
  options.sort((a, b) => a.text.localeCompare(b.text));

  return options;
}

/**
 * Extract unique user UUIDs from USER-grouped token usage data
 * and convert them to user filter options with display names
 *
 * @param usageData Token usage data grouped by USER
 * @param usersMap Map of user UUIDs to user objects
 * @returns Array of user filter options sorted alphabetically
 */
export function extractTokenUserFilterOptions(
  usageData: TokenUsageWithBreakdown[] | undefined,
  usersMap: Map<string, User>
): UserFilterOption[] {
  if (!usageData || usageData.length === 0) {
    return [];
  }

  const uniqueUserUUIDs = new Set<string>();

  // Extract unique user UUIDs from usage data
  // Normalize to strip any "users/" prefix, similar to workspace extraction
  usageData.forEach((usageItem) => {
    const usageBreakdown = usageItem.usageBreakdown || [];
    usageBreakdown.forEach((breakdown) => {
      if (breakdown.group) {
        // Normalize to strip any prefix (e.g., "users/uuid" -> "uuid")
        const normalizedId = normalizeGroupId(breakdown.group);
        uniqueUserUUIDs.add(normalizedId);
      }
    });
  });

  // Convert UUIDs to filter options with display names
  const options: UserFilterOption[] = [];
  uniqueUserUUIDs.forEach((uuid) => {
    const lookupKey = `users/${uuid}`;
    const user = usersMap.get(lookupKey);

    if (user) {
      const displayName = user.displayName || user.loginPrincipal || uuid;
      options.push({
        key: uuid,
        text: displayName,
      });
    } else {
      // User not found in map, use UUID as fallback
      console.warn('[extractTokenUserFilterOptions] User not found in map:', uuid);
      options.push({
        key: uuid,
        text: uuid,
      });
    }
  });

  // Sort alphabetically by display name
  options.sort((a, b) => a.text.localeCompare(b.text));

  return options;
}
